import React, { useState, useEffect, useRef } from "react";

/**
 * TypeWriter
 * Animates text letter by letter when the element enters the viewport.
 *
 * Props:
 *  - text        {string}   Full text to animate
 *  - speed       {number}   ms per character (default 60)
 *  - className   {string}   Class(es) for the wrapping element
 *  - tag         {string}   HTML tag to render (default "span")
 *  - cursor      {boolean}  Show blinking cursor while typing (default true)
 */
function TypeWriter({
  text = "",
  speed = 60,
  className = "",
  tag: Tag = "span",
  cursor = true,
}) {
  const [displayed, setDisplayed] = useState("");
  const [typing, setTyping] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTyping(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!typing) return;
    setDisplayed("");
    let i = 0;
    const id = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) clearInterval(id);
    }, speed);
    return () => clearInterval(id);
  }, [typing, text, speed]);

  const done = displayed.length === text.length;

  return (
    <Tag ref={ref} className={className}>
      {displayed}
      {cursor && !done && (
        <span className="tw-cursor">|</span>
      )}
    </Tag>
  );
}

export default TypeWriter;
